/**
 * Feature Gating Hook
 * 
 * Provides tier-based feature access control for the frontend.
 * 
 * IMPORTANT: Uses effective_tier for authorization (not current_tier which is for display)
 * 
 * UX Rules:
 * - Locked features appear disabled/read-only (not hidden)
 * - Starter users see "Upgrade to Pro" CTA
 * - Pro users see "Contact us" CTA for Studio features
 * - Studio/Legacy users see NO upgrade CTAs (self-serve blocked)
 * - Pending upgrades show "Upgrade pending confirmation"
 * - Pending downgrades keep features unlocked until renewal
 */

import { useMemo } from "react";
import { useAuth } from "../App";

// Tier capability map - defines which features are available per tier
const TIER_CAPABILITIES = {
  starter: {
    // Core features
    booking_requests: true,
    basic_contracts: true,
    email_notifications: true,
    portfolio_site: true,
    basic_galleries: true,
    
    // Limited features
    max_portfolio_photos: 10,
    max_galleries: 5,
    max_clients: 50,
    
    // Pro features - NOT available
    sell_downloads: false,
    accept_payments: false,
    custom_branding: false,
    watermarking: false,
    client_favorites: false,
    advanced_contracts: false,
    calendar_sync: false,
    analytics: false,
    
    // Studio features - NOT available
    team_members: false,
    white_label: false,
    api_access: false,
    priority_support: false,
    custom_domain: false,
  },
  
  pro: {
    // All Starter features
    booking_requests: true,
    basic_contracts: true,
    email_notifications: true,
    portfolio_site: true,
    basic_galleries: true,
    
    // Increased limits
    max_portfolio_photos: 50,
    max_galleries: 25,
    max_clients: 250,
    
    // Pro features - available
    sell_downloads: true,
    accept_payments: true,
    custom_branding: true,
    watermarking: true,
    client_favorites: true,
    advanced_contracts: true,
    calendar_sync: true,
    analytics: true,
    
    // Studio features - NOT available
    team_members: false,
    white_label: false,
    api_access: false,
    priority_support: false,
    custom_domain: false,
  },
  
  studio: {
    // All features unlocked
    booking_requests: true,
    basic_contracts: true,
    email_notifications: true,
    portfolio_site: true,
    basic_galleries: true,
    
    // Unlimited/high limits
    max_portfolio_photos: 500,
    max_galleries: null, // unlimited
    max_clients: null, // unlimited
    
    // Pro features
    sell_downloads: true,
    accept_payments: true,
    custom_branding: true,
    watermarking: true,
    client_favorites: true,
    advanced_contracts: true,
    calendar_sync: true,
    analytics: true,
    
    // Studio features - available
    team_members: true,
    white_label: true,
    api_access: true,
    priority_support: true,
    custom_domain: true,
  },
  
  // Legacy tier has same access as Studio
  legacy: {
    booking_requests: true,
    basic_contracts: true,
    email_notifications: true,
    portfolio_site: true,
    basic_galleries: true,
    max_portfolio_photos: 500,
    max_galleries: null,
    max_clients: null,
    sell_downloads: true,
    accept_payments: true,
    custom_branding: true,
    watermarking: true,
    client_favorites: true,
    advanced_contracts: true,
    calendar_sync: true,
    analytics: true,
    team_members: true,
    white_label: true,
    api_access: true,
    priority_support: true,
    custom_domain: true,
  }
};

// Feature descriptions and upgrade info
const FEATURE_INFO = {
  sell_downloads: {
    name: "Sell Downloads",
    description: "Enable photo sales and digital downloads",
    requiredTier: "pro"
  },
  accept_payments: {
    name: "Accept Payments",
    description: "Accept booking payments and deposits",
    requiredTier: "pro"
  },
  custom_branding: {
    name: "Custom Branding",
    description: "Customize colors, logos, and branding",
    requiredTier: "pro"
  },
  watermarking: {
    name: "Watermarking",
    description: "Add watermarks to gallery photos",
    requiredTier: "pro"
  },
  client_favorites: {
    name: "Client Favorites",
    description: "Let clients mark favorite photos",
    requiredTier: "pro"
  },
  advanced_contracts: {
    name: "Advanced Contracts",
    description: "Custom contract templates and e-signatures",
    requiredTier: "pro"
  },
  calendar_sync: {
    name: "Calendar Sync",
    description: "Sync with Google Calendar",
    requiredTier: "pro"
  },
  analytics: {
    name: "Analytics",
    description: "View detailed analytics and reports",
    requiredTier: "pro"
  },
  team_members: {
    name: "Team Members",
    description: "Add team members and assign roles",
    requiredTier: "studio"
  },
  white_label: {
    name: "White Label",
    description: "Remove 360Lumina branding completely",
    requiredTier: "studio"
  },
  api_access: {
    name: "API Access",
    description: "Access to API for custom integrations",
    requiredTier: "studio"
  },
  priority_support: {
    name: "Priority Support",
    description: "24/7 priority customer support",
    requiredTier: "studio"
  },
  custom_domain: {
    name: "Custom Domain",
    description: "Use your own domain for client portals",
    requiredTier: "studio"
  }
};

/**
 * Hook to check feature access based on current subscription tier
 * 
 * IMPORTANT: Uses effective_tier for authorization (not current_tier which is for display)
 * 
 * @returns {Object} Feature gate utilities
 */
export function useFeatureGate() {
  const { user } = useAuth();
  
  // Use effective_tier for authorization (backend uses this for gating)
  // Falls back to current_plan_key for backwards compatibility
  const effectiveTier = useMemo(() => {
    if (!user) return "starter";
    
    // During trial, treat as their trial plan tier (usually pro)
    if (user.subscription_status === "trialing") {
      return user.effective_tier || user.current_plan_key || "pro";
    }
    
    // Use effective_tier if available, otherwise current_plan_key
    return user.effective_tier || user.current_plan_key || "starter";
  }, [user]);
  
  // For display purposes, use current_tier
  const displayTier = useMemo(() => {
    if (!user) return "starter";
    return user.current_tier || user.current_plan_key || "starter";
  }, [user]);
  
  const isTrialing = user?.subscription_status === "trialing";
  const isActive = user?.subscription_status === "active";
  const isPastDue = user?.subscription_status === "past_due";
  const hasUsedTrial = user?.has_used_trial || false;
  
  // Pending tier for scheduled downgrades
  const pendingTier = user?.pending_tier || null;
  const tierChangeState = user?.tier_change_state || "no_change";
  
  /**
   * Check if a feature is available for the current tier
   * Uses effective_tier for authorization
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} Whether the feature is available
   */
  const hasFeature = (featureKey) => {
    const capabilities = TIER_CAPABILITIES[effectiveTier] || TIER_CAPABILITIES.starter;
    return !!capabilities[featureKey];
  };
  
  /**
   * Get the limit for a feature (for numeric limits like max_galleries)
   * @param {string} featureKey - The feature key
   * @returns {number|null} The limit value, or null if unlimited
   */
  const getFeatureLimit = (featureKey) => {
    const capabilities = TIER_CAPABILITIES[effectiveTier] || TIER_CAPABILITIES.starter;
    return capabilities[featureKey];
  };
  
  /**
   * Get info about a locked feature
   * @param {string} featureKey - The feature key
   * @returns {Object} Feature info including required tier
   */
  const getFeatureInfo = (featureKey) => {
    return FEATURE_INFO[featureKey] || {
      name: featureKey,
      description: "Premium feature",
      requiredTier: "pro"
    };
  };
  
  /**
   * Check if current tier can upgrade to access a feature
   * @param {string} featureKey - The feature key
   * @returns {string|null} The tier name to upgrade to, or null if already available
   */
  const getUpgradeTier = (featureKey) => {
    if (hasFeature(featureKey)) return null;
    
    const info = getFeatureInfo(featureKey);
    return info.requiredTier;
  };
  
  /**
   * Check if self-serve plan changes are blocked (Studio/Legacy)
   */
  const isSelfServeBlocked = effectiveTier === "studio" || effectiveTier === "legacy";
  
  /**
   * Check if upgrade is pending confirmation (waiting for invoice.paid)
   */
  const isUpgradePending = tierChangeState === "upgrade_pending_payment" || 
                            tierChangeState === "upgrade_checkout_created";
  
  /**
   * Check if downgrade is scheduled (features remain unlocked)
   */
  const isDowngradeScheduled = tierChangeState === "downgrade_scheduled" || 
                                tierChangeState === "downgrade_pending_renewal";
  
  /**
   * Get the appropriate CTA action for a locked feature
   * Returns: { type: 'upgrade' | 'contact' | 'none' | 'pending', label: string, action: function }
   */
  const getUpgradeCTA = (featureKey) => {
    if (hasFeature(featureKey)) {
      return { type: 'none', label: '', action: null };
    }
    
    // If upgrade is pending, show pending message
    if (isUpgradePending) {
      return { 
        type: 'pending', 
        label: 'Upgrade pending confirmation', 
        action: null 
      };
    }
    
    // Studio/Legacy users should NOT see self-serve upgrade CTAs
    if (isSelfServeBlocked) {
      return { type: 'none', label: '', action: null };
    }
    
    const requiredTier = getUpgradeTier(featureKey);
    
    // Starter users upgrading to Pro features
    if (effectiveTier === 'starter' && requiredTier === 'pro') {
      return { 
        type: 'upgrade', 
        label: 'Upgrade to Pro', 
        action: () => window.location.href = '/pricing' 
      };
    }
    
    // Pro users wanting Studio features - must contact
    if (effectiveTier === 'pro' && requiredTier === 'studio') {
      return { 
        type: 'contact', 
        label: 'Contact us', 
        action: () => window.location.href = '/studio-contact' 
      };
    }
    
    // Default upgrade action
    return { 
      type: 'upgrade', 
      label: `Upgrade to ${requiredTier?.charAt(0).toUpperCase()}${requiredTier?.slice(1)}`, 
      action: () => window.location.href = '/pricing' 
    };
  };
  
  return {
    currentTier: effectiveTier,  // For authorization
    displayTier,                  // For display
    effectiveTier,
    pendingTier,
    tierChangeState,
    isTrialing,
    isActive,
    isPastDue,
    hasUsedTrial,
    isSelfServeBlocked,
    isUpgradePending,
    isDowngradeScheduled,
    hasFeature,
    getFeatureLimit,
    getFeatureInfo,
    getUpgradeTier,
    getUpgradeCTA,
    capabilities: TIER_CAPABILITIES[effectiveTier] || TIER_CAPABILITIES.starter
  };
}

export { TIER_CAPABILITIES, FEATURE_INFO };
